import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Slider from 'react-slick'
import { startSession, endSession } from '../services/usageTracking'
import './RadioView.css'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

const API_BASE = 'https://backend.alephmedia.ro/api'

const DEFAULT_RADIO = {
  id: null,
  title: '',
  excerpt: '',
  motto: '',
  link: '',
  audio_stream_url: '',
  metadata_url: '',
  image: null,
  gradient_start: '',
  gradient_middle: '',
  gradient_end: ''
}

const ArrowButton = ({ direction, onClick }) => {
  const isNext = direction === 'next'
  return (
    <button
      type="button"
      className={`radio-view-arrow radio-view-arrow-${direction}`}
      onClick={onClick}
      aria-label={isNext ? 'Următorul set de radiouri' : 'Setul anterior de radiouri'}
    >
      {isNext ? (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  )
}

const RadioView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const audioRef = useRef(null)
  const [radio, setRadio] = useState(DEFAULT_RADIO)
  const [otherRadios, setOtherRadios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [currentTrack, setCurrentTrack] = useState({ artist: '', title: '', image: '' })
  const [trackHistory, setTrackHistory] = useState([])
  const usageSessionIdRef = useRef(null) // ID-ul sesiunii de tracking
  const playStartTimeRef = useRef(null) // Timpul când a început play-ul

  useEffect(() => {
    if (!id) {
      navigate('/v3.0')
    }
  }, [id, navigate])

  useEffect(() => {
    const stateRadio = location.state?.radio
    if (stateRadio) {
      setRadio({ ...DEFAULT_RADIO, ...stateRadio })
    }

    async function loadRadio() {
      if (!id) return
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_BASE}/radios/${id}`)
        if (!response.ok) {
          throw new Error(`Nu am putut încărca radioul. Cod: ${response.status}`)
        }
        const json = await response.json()
        if (!json.success || !json.data) {
          throw new Error('Răspuns invalid de la server.')
        }
        setRadio({ ...DEFAULT_RADIO, ...json.data })
      } catch (err) {
        console.error('loadRadio failed', err)
        setError(err.message || 'Nu am putut încărca radioul selectat.')
      } finally {
        setLoading(false)
      }
    }

    loadRadio()
  }, [id, location.state])

  useEffect(() => {
    async function loadOtherRadios() {
      try {
        const response = await fetch(`${API_BASE}/radios`)
        if (!response.ok) return
        const json = await response.json()
        if (json.success && json.data) {
          const filtered = json.data.filter(r => r.id !== parseInt(id))
          setOtherRadios(filtered)
        }
      } catch (err) {
        console.error('loadOtherRadios failed', err)
      }
    }

    if (id) {
      loadOtherRadios()
    }
  }, [id])

  useEffect(() => {
    if (!radio.metadata_url) return

    const fetchMetadata = async () => {
      try {
        const response = await fetch(radio.metadata_url)
        if (!response.ok) return
        const data = await response.json()
        
        if (data.nowPlaying) {
          setCurrentTrack({
            artist: data.nowPlaying.artist || '',
            title: data.nowPlaying.title || '',
            image: data.nowPlaying.image || ''
          })
        }

        if (data.history && Array.isArray(data.history)) {
          setTrackHistory(data.history.map(track => ({
            artist: track.artist || '',
            title: track.title || '',
            image: track.image || '',
            starttime: track.starttime || ''
          })))
        }
      } catch (err) {
      }
    }

    fetchMetadata()
    const interval = setInterval(fetchMetadata, 10000)

    return () => clearInterval(interval)
  }, [radio.metadata_url])

  // Cleanup când componenta se unmount
  useEffect(() => {
    return () => {
      // Finalizăm sesiunea de tracking dacă există
      if (usageSessionIdRef.current) {
        endSession(usageSessionIdRef.current)
        usageSessionIdRef.current = null
        playStartTimeRef.current = null
      }
    }
  }, [])

  const handleTogglePlay = () => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
      // Finalizăm tracking-ul
      if (usageSessionIdRef.current && playStartTimeRef.current) {
        endSession(usageSessionIdRef.current)
        usageSessionIdRef.current = null
        playStartTimeRef.current = null
      }
    } else {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true)
          // Începem tracking-ul când începe play-ul
          if (!usageSessionIdRef.current && radio.id) {
            usageSessionIdRef.current = startSession('radio', radio.id, {
              radio_title: radio.title || ''
            })
            playStartTimeRef.current = Date.now()
          }
        })
        .catch(err => console.error('Play failed', err))
    }
  }

  const handleToggleMute = () => {
    if (!audioRef.current) return
    const newMuted = !isMuted
    audioRef.current.muted = newMuted
    setIsMuted(newMuted)
  }

  const handleVolumeChange = (event) => {
    if (!audioRef.current) return
    const newVolume = Number(event.target.value)
    audioRef.current.volume = newVolume
    setVolume(newVolume)
    if (newVolume > 0 && isMuted) {
      audioRef.current.muted = false
      setIsMuted(false)
    }
  }

  const volumePercent = useMemo(() => Math.max(0, Math.min(100, volume * 100)), [volume])
  const volumeIcon = useMemo(() => {
    if (isMuted || volumePercent === 0) {
      return 'muted'
    }
    if (volumePercent < 50) {
      return 'low'
    }
    return 'high'
  }, [isMuted, volumePercent])

  const gradientStyle = useMemo(() => {
    if (!radio.gradient_start && !radio.gradient_end) return {}
    
    const start = radio.gradient_start || '#fff'
    const end = radio.gradient_end || '#000'
    const middle = radio.gradient_middle

    if (middle) {
      return { background: `linear-gradient(135deg, ${start} 0%, ${middle} 50%, ${end} 100%)` }
    }
    return { background: `linear-gradient(135deg, ${start} 0%, ${end} 100%)` }
  }, [radio.gradient_start, radio.gradient_middle, radio.gradient_end])

  const desktopImage = radio.image
  const displayImage = desktopImage || '/assets/img/logo-aleph-rosu.png'

  const renderRadioCard = (radioItem, index) => {
    const cardImage = radioItem.image || '/assets/img/logo-aleph-rosu.png'

    return (
      <div
        key={radioItem.id ?? `radio-${index}`}
        className="radio-view-card"
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/v3.0/radios/${radioItem.id}`, { state: { radio: radioItem } })}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            navigate(`/v3.0/radios/${radioItem.id}`, { state: { radio: radioItem } })
          }
        }}
      >
        <div className="radio-view-card-image">
          <img src={cardImage} alt={radioItem.title} loading="lazy" />
        </div>
        <div className="radio-view-card-title">{radioItem.title}</div>
        {radioItem.motto && <div className="radio-view-card-subtitle">{radioItem.motto}</div>}
      </div>
    )
  }

  return (
    <div className="radio-view-root">
      <div className="radio-view-header">
        <button type="button" className="radio-view-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="41" height="34" viewBox="0 0 41 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.3333 21.6667L3 12.3333M3 12.3333L12.3333 3M3 12.3333H28.6667C31.142 12.3333 33.516 13.3167 35.2663 15.067C37.0167 16.8173 38 19.1913 38 21.6667C38 24.142 37.0167 26.516 35.2663 28.2663C33.516 30.0167 31.142 31 28.6667 31H26.3333" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {loading && <div className="radio-view-status">Se încarcă radioul...</div>}
      {error && <div className="radio-view-status radio-view-status-error">{error}</div>}

      {!loading && !error && (
        <div className="radio-view-content">
          <div className="radio-view-hero">
            <div className="radio-view-poster" style={gradientStyle}>
              {displayImage ? (
                <img src={displayImage} alt={radio.title || 'Radio'} />
              ) : (
                <div className="radio-view-placeholder">{radio.title?.[0] ?? '?'}</div>
              )}
            </div>
            <h1>{radio.title}</h1>
            {(radio.excerpt || radio.motto) && (
              <p className="radio-view-motto">{radio.motto || radio.excerpt}</p>
            )}
          </div>

          <div className="radio-view-player">
            <audio ref={audioRef} src={radio.audio_stream_url} preload="none" />
            
            <div className="radio-view-player-controls">
              <button
                type="button"
                className="radio-view-play-btn"
                onClick={handleTogglePlay}
              >
                {isPlaying ? (
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                )}
              </button>

              <div className="radio-view-player-info">
                {currentTrack.artist && currentTrack.title ? (
                  <>
                    <div className="radio-view-track-title">{currentTrack.title}</div>
                    <div className="radio-view-track-artist">{currentTrack.artist}</div>
                  </>
                ) : (
                  <div className="radio-view-track-title">{radio.title}</div>
                )}
              </div>

              <div className="radio-view-volume-controls">
                <button type="button" className="radio-view-volume-btn" onClick={handleToggleMute}>
                  {volumeIcon === 'muted' && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <line x1="23" y1="9" x2="17" y2="15" />
                      <line x1="17" y1="9" x2="23" y2="15" />
                    </svg>
                  )}
                  {volumeIcon === 'low' && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    </svg>
                  )}
                  {volumeIcon === 'high' && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    </svg>
                  )}
                </button>

                <div className="radio-view-volume-slider">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={handleVolumeChange}
                    onInput={handleVolumeChange}
                    style={{ '--progress': `${volumePercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {trackHistory.length > 0 && (
            <div className="radio-view-tracks">
              <h3>Melodii recente</h3>
              <div className="radio-view-tracks-list">
                {trackHistory.map((track, index) => {
                  let coverUrl = radio.image || '/assets/img/logo-aleph-rosu.png'
                  
                  if (track.image && track.image !== 'covers/No_Cover.jpg') {
                    coverUrl = `https://www.smartradio.ro/winmedia/${track.image}`
                  }
                  
                  return (
                    <div key={`track-${index}`} className="radio-view-track-item">
                      <div className="radio-view-track-cover">
                        <img src={coverUrl} alt={track.title} loading="lazy" />
                      </div>
                      <div className="radio-view-track-details">
                        <div className="radio-view-track-item-title">{track.title}</div>
                        <div className="radio-view-track-item-artist">{track.artist}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {otherRadios.length > 0 && (
        <div className="radio-view-others">
          <h2>Alte radiouri</h2>
          <div className="radio-view-others-section">
            <Slider
              className="radio-view-slider"
              slidesToShow={6}
              slidesToScroll={6}
              infinite={false}
              arrows
              dots
              nextArrow={<ArrowButton direction="next" />}
              prevArrow={<ArrowButton direction="prev" />}
              responsive={[
                { breakpoint: 1280, settings: { slidesToShow: 5, slidesToScroll: 5 } },
                { breakpoint: 1024, settings: { slidesToShow: 4, slidesToScroll: 4 } },
                { breakpoint: 768, settings: { slidesToShow: 3, slidesToScroll: 3, arrows: false, dots: true } },
                { breakpoint: 640, settings: { slidesToShow: 2, slidesToScroll: 2, arrows: false, dots: true } }
              ]}
            >
              {otherRadios.map((radioItem, index) => renderRadioCard(radioItem, index))}
            </Slider>
          </div>
        </div>
      )}
    </div>
  )
}

export default RadioView

